import { ChevronDown, ChevronRight } from '@carbon/react/icons';
import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { ProgressBar } from '@carbon/react';
import { uniqueId } from 'lodash-es';
import plur from 'plur';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Pagination } from 'components/pagination';
import { useLatest } from 'lib/hooks/use-latest';
import {
  itemRangeText,
  pageRangeText,
} from 'components/search/common/format-helpers';
import { PAGE_SIZES } from 'utils/page-sizes';
import { DataTableProvider } from './context';
import styles from './data-table.module.scss';
import { DefaultSubTable } from './default-sub-table';
import { FilterPanel } from './filter/filter-panel';
import { columnId, useColumnWidths, useTableState } from './hooks';
import { SelectAllHeader, SelectCheckboxCell } from './selection-column';
import { StackedCell } from './stacked-cell';
import { TableBody } from './table-body';
import { TableError } from './table-error';
import { TableHeader } from './table-header';
import { TableToolbar } from './toolbar/table-toolbar';
import {
  DataTableColumnMeta,
  DataTableProps,
  InfiniteScrollConfig,
} from './types';
import {
  formatCellValue,
  getAlignment,
  getFilterFnForType,
  RESERVED_COLUMN_IDS,
  RESERVED_COLUMN_SIZE,
} from './utils';

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * A feature-rich data table component built on TanStack Table.
 * Supports filtering (global and column), sorting, column resizing/reordering/pinning,
 * infinite scroll, pagination, export, and state persistence.
 * @param {DataTableProps} props - Table configuration
 * @param {TData[]} props.data - Array of data objects to display
 * @param {ColumnDef[]} props.columns - Column definitions
 * @param {Function} props.getRowId - Function to extract unique row ID
 * @param {boolean} [props.isLoading] - Loading state indicator
 * @param {Error | null} [props.error] - Error to display
 * @param {object} [props.initialTableState] - Initial state configuration
 * @param {string} [props.persistenceKey] - Key for localStorage persistence
 * @param {object} [props.config] - Feature configuration
 * @param {Function} [props.renderFilterPanel] - Render prop for filter panel
 * @param {object} [props.panelFilters] - Consumer-held panel filters that Reset Filters counts and clears
 * @param {object} [props.scroll] - Scroll/pagination mode (paginated client/server or infinite)
 * @param {Function} [props.onPaginationChange] - Pagination change callback (full TableState snapshot)
 * @param {Function} [props.onSortChange] - Sort change callback (full TableState snapshot)
 * @param {Function} [props.onFilterChange] - Filter change callback (full TableState snapshot)
 * @param {Function} [props.onGlobalSearchChange] - Global search change callback (full TableState snapshot)
 * @param {Function} [props.onLoadMore] - Fired in infinite mode when virtualizer hits near-bottom
 * @param {Function} [props.onSelectionChange] - Selection change callback (full TableState snapshot)
 * @param {Function} [props.onExpansionChange] - Expansion change callback (full TableState snapshot)
 * @param {Function} [props.onVisibleColumnsChange] - Shown column ids in on-screen order, after mount and on every change
 * @return {JSX.Element} The complete data table element
 */
export function DataTable<TData extends Record<string, unknown>>(
  props: DataTableProps<TData>
) {
  const {
    data,
    columns,
    getRowId,

    // Loading/Error
    isLoading = false,
    error = null,

    // Selection lock — disable all row-selection interaction without
    // losing current selection state. Works across all `selectionType`
    // values: multi (checkboxes disabled), single (row-click gated),
    // undefined (row-click callback gated).
    disableSelection = false,

    // Initial state (unified)
    initialTableState = {},

    // Controlled state (opt-in, per slice)
    controlledState,

    // Persistence
    persistenceKey,

    // Feature configuration
    config = {},

    // Filter panel
    renderFilterPanel,
    panelFilters,

    // Scroll / pagination
    scroll,
    maxBodyHeight,
    emptyMessage,
    pageSizes = PAGE_SIZES,

    // Row expansion
    renderRowSubComponent,
    getRowCanExpand: getRowCanExpandProp,
    getExpanderClassName,

    // Toolbar actions
    leftToolbarActions,
    fillLeftToolbarActions,
    leadingToolbarActions,
    rightToolbarActions,
    betweenToolbarAndContent,

    // Per-event callbacks (phase 6)
    onPaginationChange,
    onSortChange,
    onFilterChange,
    onGlobalSearchChange,
    onLoadMore,
    onSelectionChange,
    onExpansionChange,
    onColumnOrderChange,
    onColumnPinningChange,
    onColumnVisibilityChange,
    onColumnSizingChange,
    onVisibleColumnsChange,

    // Highlighting
    highlightedRowIds,
    indeterminateRowIds,

    searchPlaceholder,
  } = props;

  // Extract config with defaults
  const {
    columnResizing: enableColumnResizing = true,
    columnReordering: enableColumnReordering = true,
    columnPinning: enableColumnPinning = true,
    sorting: enableSorting = true,
    multiSort: enableMultiSort = true,
    sortingRemoval: enableSortingRemoval = true,
    columnVisibility: enableColumnVisibility = true,
    globalSearch: enableGlobalSearch = true,
    columnFilters: enableColumnFilters = true,
    centerHeaders = false,
    export: exportProp,
    expandableRows = false,
    expandAllToggle = false,
    expandAllInHeader = false,
    fitContent = false,
    sizeToContent = false,
    selectionType,
    maxSelections,
    rowClickSelection = false,
    isBulkSelectEnabled = true,
    hideHeader = false,
    hideFooter = false,
  } = config;
  const enableCheckboxSelection = selectionType === 'multi';

  const hasSelectionLimit = enableCheckboxSelection && !!maxSelections;

  // Normalize export prop: true → {}, object → object, falsy → undefined
  const exportConfig = exportProp === true ? {} : exportProp || undefined;
  const showExport = Boolean(exportProp);

  // Show reset button when any customization feature is enabled
  const showReset =
    enableColumnResizing ||
    enableColumnReordering ||
    enableColumnPinning ||
    enableColumnVisibility ||
    enableSorting;

  // Show toolbar when any toolbar feature is enabled
  const showToolbar =
    enableGlobalSearch || enableColumnFilters || showReset || showExport;

  // ─── Scroll / Pagination Mode Detection ───
  // Explicit per phase 6: `serverDriven: true` flag on paginated ScrollConfig
  // (replaces the old implicit `!!loadData` derivation).
  const isInfiniteScroll = scroll?.mode === 'infinite';
  const isServerPagination =
    scroll?.mode === 'paginated' && 'serverDriven' in scroll;
  const isClientPagination =
    scroll?.mode === 'paginated' && !('serverDriven' in scroll);
  const isServerDriven = isServerPagination || isInfiniteScroll;
  const hasPagination = scroll?.mode === 'paginated';
  const totalCountProp =
    scroll && 'totalCount' in scroll ? scroll.totalCount : undefined;

  // manualFiltering / manualSorting / manualPagination all derive from
  // `isServerDriven`. Previously `manualSorting = !!onSortChange` which
  // silently broke client sorting if a caller wired sort observation —
  // phase 6 bug fix.
  const manualFiltering = isServerDriven;
  const manualSorting = isServerDriven;

  // Destructure initial state with defaults
  const {
    columnVisibility: initialColumnVisibility = {},
    columnOrder: initialColumnOrder,
    columnPinning: initialColumnPinning = { left: [] },
    sortColumn: initialSortColumn,
    sortDirection: initialSortDirection = 'asc',
    expanded: initialExpanded,
    columnFilters: initialColumnFilters,
    page: initialPage,
    pageSize: initialPageSize,
  } = initialTableState;

  // ─── Expansion Capability (renderRowSubComponent or config.expandableRows) ───
  const hasExpansion = !!renderRowSubComponent || expandableRows;

  // Unique prefix for checkbox IDs — generated once per instance, never changes
  const tableUid = useRef(uniqueId('dt-')).current;

  // ─── Table State (owns all data-affecting state) ───
  // The hook injects `_select` / `_expander` prefix columns into its column
  // defaults internally, driven by the flags below. Callers pass raw
  // `columns` — no upstream prefix massaging.
  const tableState = useTableState({
    persistenceKey,
    columns,
    enableCheckboxSelection,
    hasExpansion,
    columnReordering: enableColumnReordering,
    initialColumnVisibility,
    initialColumnOrder,
    initialColumnPinning,
    initialColumnFilters,
    initialGlobalFilter: initialTableState.globalFilter,
    initialSortColumn,
    initialSortDirection,
    initialPage,
    initialPageSize,
    initialExpanded,
    isServerDriven,
    initialSelection: initialTableState.selection,
    controlledState,
    totalCountProp,
    onPaginationChange,
    onSortChange,
    onFilterChange,
    onGlobalSearchChange,
    onSelectionChange,
    onExpansionChange,
    onColumnOrderChange,
    onColumnPinningChange,
    onColumnVisibilityChange,
    onColumnSizingChange,
  });

  // ─── Selection Column (auto-added when checkboxSelection is true) ───
  // The cell + header are extracted React components that read selection
  // state via DataTableContext. The column def closes over only stable values
  // (flags + tableUid), so this useMemo doesn't re-run on selection changes
  // — no remount, no ref escape hatch needed.
  const selectionColumn: ColumnDef<TData> | null = useMemo(() => {
    if (!enableCheckboxSelection) return null;

    return {
      id: RESERVED_COLUMN_IDS.SELECT,
      header:
        hasSelectionLimit || !isBulkSelectEnabled
          ? ''
          : ({ table }) => (
              <SelectAllHeader
                table={table}
                tableUid={tableUid}
                isServerDriven={isServerDriven}
              />
            ),
      cell: ({ row }) => (
        <SelectCheckboxCell
          row={row}
          tableUid={tableUid}
          hasSelectionLimit={hasSelectionLimit}
          maxSelections={maxSelections}
        />
      ),
      size: RESERVED_COLUMN_SIZE,
      minSize: RESERVED_COLUMN_SIZE,
      maxSize: RESERVED_COLUMN_SIZE,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      meta: {
        toggleColumnFilter: false,
        enablePinning: false,
        enableReordering: false,
        isExportable: false,
      },
    };
  }, [
    enableCheckboxSelection,
    hasSelectionLimit,
    isBulkSelectEnabled,
    maxSelections,
    tableUid,
    isServerDriven,
  ]);

  // ─── Expander Column (auto-added when expansion is enabled) ───
  const expanderColumn: ColumnDef<TData> | null = useMemo(() => {
    if (!hasExpansion) return null;

    return {
      id: RESERVED_COLUMN_IDS.EXPANDER,
      header: expandAllInHeader
        ? ({ table: headerTable }) => {
            // Not getIsAllRowsExpanded: it ignores getCanExpand and pagination.
            const expandable = headerTable
              .getRowModel()
              .rows.filter((r) => r.getCanExpand());
            const allExpanded =
              expandable.length > 0 &&
              expandable.every((r) => r.getIsExpanded());
            const label = allExpanded ? 'Collapse all rows' : 'Expand all rows';
            return (
              <button
                type="button"
                className={styles.expandButton}
                aria-label={label}
                title={label}
                aria-expanded={allExpanded}
                disabled={expandable.length === 0}
                onClick={() => headerTable.toggleAllRowsExpanded(!allExpanded)}
              >
                {allExpanded ? <ChevronDown /> : <ChevronRight />}
              </button>
            );
          }
        : '',
      size: RESERVED_COLUMN_SIZE,
      minSize: RESERVED_COLUMN_SIZE,
      maxSize: RESERVED_COLUMN_SIZE,
      enableSorting: false,
      enableResizing: false,
      enableHiding: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      meta: {
        toggleColumnFilter: false,
        enablePinning: false,
        enableReordering: false,
        isExportable: false,
      },
      cell: ({ row }) => {
        if (!row.getCanExpand()) {
          return <span className={styles.expandPlaceholder} />;
        }

        const isExpanded = row.getIsExpanded();
        const extraClassName = getExpanderClassName?.(row);
        return (
          <button
            type="button"
            className={`${styles.expandButton}${
              extraClassName ? ` ${extraClassName}` : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            title={isExpanded ? 'Collapse row' : 'Expand row'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
          </button>
        );
      },
    };
  }, [hasExpansion, getExpanderClassName, expandAllInHeader]);

  // ─── Process Columns (assign filterFn based on type, prepend selection + expander) ───
  //
  // For columns without a user-defined `cell` renderer but with `meta.type`,
  // we inject an automatic cell function that calls `formatCellValue`. This
  // means table-body can always call flexRender without needing to know whether
  // the column uses type-based formatting or a custom renderer.
  const processedColumns = useMemo(() => {
    const cols = columns.map((col) => {
      const hasUserCell = typeof col.cell === 'function';
      const processed: ColumnDef<TData> = {
        ...col,
        filterFn:
          col.filterFn ??
          (getFilterFnForType(col.meta?.type, col.meta?.format) as never),
        meta: { ...col.meta },
      };

      if (!hasUserCell && col.meta?.type) {
        const colMeta = col.meta as DataTableColumnMeta;
        processed.cell = (info) => {
          const value = info.getValue();
          if (Array.isArray(value)) {
            return (
              <StackedCell
                values={value.map((item) => formatCellValue(item, colMeta))}
                align={getAlignment(colMeta)}
              />
            );
          }
          return formatCellValue(value, colMeta);
        };
      }

      return processed;
    });

    const prefix: ColumnDef<TData>[] = [];
    if (selectionColumn) prefix.push(selectionColumn);
    if (expanderColumn) prefix.push(expanderColumn);
    return prefix.length > 0 ? [...prefix, ...cols] : cols;
  }, [columns, selectionColumn, expanderColumn]);

  // Whether any column has `meta.wrap: true`. Computed from column defs
  // (not rows) so the answer is stable across empty / loading / loaded
  // states — prevents the rendering branch from flipping between static-
  // height and measured-height layouts when data arrives.
  const wrapColumnIds = useMemo(
    () =>
      processedColumns
        .filter(
          (col) => (col.meta as DataTableColumnMeta | undefined)?.wrap === true
        )
        .map(columnId),
    [processedColumns]
  );
  const hasWrappingColumn = wrapColumnIds.length > 0;

  // ─── Effective Renderer (custom or default sub-table) ───
  // Use a ref so the default renderer callback identity stays stable across
  // column changes (avoids unmount/remount of expanded rows).
  const processedColumnsRef = useRef(processedColumns);
  processedColumnsRef.current = processedColumns;

  const defaultRenderer = useCallback(
    (row: Row<TData>) => (
      <DefaultSubTable row={row} columns={processedColumnsRef.current} />
    ),
    []
  );

  const effectiveRenderer = renderRowSubComponent
    ? renderRowSubComponent
    : expandableRows
      ? defaultRenderer
      : undefined;

  // ─── Column Widths (initial distribution + independent resize) ───
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { effectiveWidths, updateColumnWidth, resetWidths } = useColumnWidths({
    containerRef: tableContainerRef,
    columns: processedColumns,
    fitContent,
    sizeToContent,
    contentKey: data,
    wrapColumnIds,
    isLoading: isLoading,
  });

  // "Reset to default" must also clear column resizes. A resize lives in `effectiveWidths`
  // (useColumnWidths), which `resetAllCustomizations` (the atom-backed layout slices) doesn't
  // touch — so pair them. `resetWidths` empties the width map; the layout effect re-distributes
  // to defaults on the next paint. Both callees are stable, so this identity stays stable too.
  const handleResetAllCustomizations = useCallback(
    () => {
      tableState.resetAllCustomizations();
      resetWidths();
      panelFilters?.reset();
    },
    // Depend on the stable `resetAllCustomizations` useCallback, NOT the whole `tableState`
    // object (it re-memoizes on nearly every state change and would churn the context).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableState.resetAllCustomizations, resetWidths, panelFilters]
  );

  // Handle row click:
  // - No selectionType: still fire onSelectionChange for row-click consumers
  // - Single select: replace toggledNodes with [rowId] + fire callback
  // - Multi select: checkboxes own selection, so skip unless the consumer
  //   opted into `rowClickSelection`, in which case toggle just this row
  //   (same edit the checkbox cell makes, leaving `selectAll` alone)
  //
  // The body reads the latest selection through a ref, so the handler passed
  // down keeps one identity instead of changing on every click.
  const rowSelectRef = useRef<(row: TData) => void>();
  rowSelectRef.current = (row: TData) => {
    if (disableSelection) return;
    if (enableCheckboxSelection) {
      if (!rowClickSelection) return;
      // Honour the same two gates the checkbox cell does, so a row click is
      // never a way around them: a locked row stays unselectable, and a
      // selection limit still binds.
      if (row['_canSelect'] === false) return;
      const clickedId = getRowId(row);
      const isSelected = tableState.selectAll
        ? !tableState.toggledNodes.has(clickedId)
        : tableState.toggledNodes.has(clickedId);
      if (
        hasSelectionLimit &&
        !isSelected &&
        tableState.selectionCount >= maxSelections!
      ) {
        return;
      }
      const next = new Set(tableState.toggledNodes);
      if (next.has(clickedId)) {
        next.delete(clickedId);
      } else {
        next.add(clickedId);
      }
      tableState.setToggledNodes(next);
      tableState.fireSelectionChange(tableState.selectAll, next);
      return;
    }
    const rowId = getRowId(row);
    if (selectionType) {
      tableState.setSelectAll(false);
      tableState.setToggledNodes(new Set([rowId]));
    }
    onSelectionChange?.(
      tableState.buildSnapshot({
        selection: { selectAll: false, toggledNodes: [rowId] },
      })
    );
  };
  const handleRowSelect = useCallback(
    (row: TData) => rowSelectRef.current?.(row),
    []
  );

  // ─── Infinite Scroll Config ───
  // Built from `scroll.hasNextPage` + the public `onLoadMore` callback.
  // TableBody's virtualizer near-bottom trigger calls fetchNextPage which
  // fires onLoadMore with a fresh snapshot.
  const { buildSnapshot } = tableState;
  const infiniteScroll: InfiniteScrollConfig | undefined = useMemo(() => {
    if (!isInfiniteScroll || !scroll || scroll.mode !== 'infinite')
      return undefined;
    return {
      hasNextPage: scroll.hasNextPage,
      fetchNextPage: () => onLoadMore?.(buildSnapshot()),
      totalCount: scroll.totalCount,
    };
  }, [isInfiniteScroll, scroll, onLoadMore, buildSnapshot]);

  // ─── Table Instance ───
  const table = useReactTable<TData>({
    data,
    columns: processedColumns,
    state: {
      globalFilter: tableState.globalFilter,
      columnFilters: tableState.columnFilters,
      sorting: tableState.sorting,
      columnVisibility: tableState.columnVisibility,
      columnOrder: tableState.columnOrder,
      columnPinning: tableState.columnPinning,
      columnSizing: tableState.columnSizing,
      expanded: tableState.expanded,
      ...(selectionType && { rowSelection: tableState.rowSelection }),
      ...(hasPagination && { pagination: tableState.pagination }),
    },
    onGlobalFilterChange: tableState.setGlobalFilter,
    onColumnFiltersChange: tableState.setColumnFilters,
    onSortingChange: tableState.setSorting,
    onColumnVisibilityChange: tableState.setColumnVisibility,
    onColumnOrderChange: tableState.setColumnOrder,
    onColumnPinningChange: tableState.setColumnPinning,
    onColumnSizingChange: tableState.setColumnSizing,
    onExpandedChange: tableState.handleExpandedChange,
    ...(hasPagination && { onPaginationChange: tableState.setPagination }),
    ...(selectionType && {
      // No `onRowSelectionChange` wiring — selection mutations go through
      // tableState's canonical setters directly (via the selection column's
      // onChange handlers and the row-click handler). `state.rowSelection`
      // wiring above keeps `row.getIsSelected()` working for table-body
      // row highlight in client mode.
      enableRowSelection: disableSelection
        ? false
        : (row: Row<TData>) =>
            (row.original as Record<string, unknown>)['_canSelect'] !== false,
      enableMultiRowSelection: selectionType === 'multi',
    }),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    ...(isClientPagination && {
      getPaginationRowModel: getPaginationRowModel(),
    }),
    ...(isServerPagination && {
      manualPagination: true,
      rowCount: totalCountProp ?? 0,
    }),
    getRowCanExpand:
      getRowCanExpandProp ?? (effectiveRenderer ? () => true : undefined),
    manualFiltering,
    manualSorting,
    // Opt-in per table (config.sortingRemoval): default true keeps TanStack's
    // third-click-clears behavior for every existing consumer; a server-driven
    // table that must stay sorted sets it false to cycle asc⇄desc instead.
    enableSortingRemoval,
    enableSorting,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    enableMultiSort,
    // Reducer's wrapped setters reset pageIndex atomically on filter/sort/search
    // changes. Disable TanStack's auto-reset to avoid a redundant second reset.
    autoResetPageIndex: false,
    getRowId: (row, index) => getRowId(row as TData, index),
  });

  // ─── Derived State ───
  // For client pagination, getRowModel() returns already-paginated rows via getPaginationRowModel.
  // For other modes, it returns all rows.
  const displayRows = table.getRowModel().rows;
  const filteredRows = table.getFilteredRowModel().rows;
  const filteredRowCount = filteredRows.length;
  const totalRowCount = data.length;
  const hasFilters =
    tableState.globalFilter || tableState.columnFilters.length > 0;
  const panelFilterCount = panelFilters?.activeCount ?? 0;
  const activeFilterCount =
    (tableState.globalFilter ? 1 : 0) +
    tableState.columnFilters.length +
    panelFilterCount;
  const { resetAllFilters: resetTableFilters } = tableState;
  const resetAllFilters = useCallback(() => {
    resetTableFilters();
    panelFilters?.reset();
  }, [resetTableFilters, panelFilters]);

  // ─── Visible Columns Report ───
  // The shown columns in on-screen order (pinned columns render first/last, as ExportButton
  // reads them), less the injected selection/expander columns; built only when someone listens.
  // The JSON keys the effect on the list's content, and the callback is read through a ref, so
  // it reports after mount and on a real change, never for a fresh array or a new callback.
  const visibleColumnsJson = onVisibleColumnsChange
    ? JSON.stringify(
        [
          ...table.getLeftVisibleLeafColumns(),
          ...table.getCenterVisibleLeafColumns(),
          ...table.getRightVisibleLeafColumns(),
        ]
          .map((column) => column.id)
          .filter(
            (id) =>
              id !== RESERVED_COLUMN_IDS.SELECT &&
              id !== RESERVED_COLUMN_IDS.EXPANDER
          )
      )
    : '';
  const onVisibleColumnsChangeRef = useLatest(onVisibleColumnsChange);
  useEffect(() => {
    if (visibleColumnsJson) {
      onVisibleColumnsChangeRef.current?.(
        JSON.parse(visibleColumnsJson) as string[]
      );
    }
  }, [visibleColumnsJson, onVisibleColumnsChangeRef]);

  // Compute effective selected row ID for single-select visual highlighting.
  // In single-select, the row click handler writes the clicked rowId into
  // toggledNodes (selectAll stays false). In multi mode, row highlighting
  // goes through TanStack's row.getIsSelected() via the derived rowSelection.
  const effectiveSelectedRowId = useMemo(() => {
    if (enableCheckboxSelection) return null;
    if (!selectionType) return null;
    return [...tableState.toggledNodes][0] ?? null;
  }, [enableCheckboxSelection, selectionType, tableState.toggledNodes]);

  // ─── Derive Loading States ───
  // Unified loading model (no skeleton): a thin progress bar covers BOTH the
  // initial load and refetches, keeping the table mounted so scroll survives.
  // The bottom spinner still owns infinite-scroll next-page fetches.
  const isFetchingNextPage = isLoading && isInfiniteScroll && data.length > 0;
  const showProgress = isLoading && !isFetchingNextPage;
  // Dim + block interaction only when there are existing rows to dim (refetch
  // over data); on the initial load there's nothing underneath to fade.
  const showStaleRows = isLoading && data.length > 0 && !isFetchingNextPage;

  // ─── Render Content ───
  const renderTableContent = () => {
    // Error state with no columns - show full error (can't render table structure)
    if (error && columns.length === 0) {
      return <TableError error={error} />;
    }

    // Table (TableBody handles empty + loading + error states internally)
    return (
      <div
        ref={tableContainerRef}
        data-table-container
        className={
          showStaleRows
            ? `${styles.tableWrapper} ${styles.tableWrapperStale}`
            : styles.tableWrapper
        }
      >
        <TableBody
          rows={displayRows}
          isLoading={isLoading}
          maxBodyHeight={maxBodyHeight}
          emptyMessage={emptyMessage}
          infiniteScroll={infiniteScroll}
          isFetchingNextPage={isFetchingNextPage}
          selectedRowId={effectiveSelectedRowId}
          onRowSelect={handleRowSelect}
          columnCount={table.getVisibleLeafColumns().length}
          tableWidth={table
            .getVisibleLeafColumns()
            .reduce(
              (sum, column) =>
                sum + (effectiveWidths[column.id] ?? column.getSize()),
              0
            )}
          effectiveWidths={effectiveWidths}
          renderRowSubComponent={effectiveRenderer}
          hasWrappingColumn={hasWrappingColumn}
          error={error}
          highlightedRowIds={highlightedRowIds}
          renderHeader={
            hideHeader
              ? undefined
              : () => (
                  <TableHeader
                    table={table}
                    enableSorting={enableSorting}
                    enableColumnReordering={enableColumnReordering}
                    enableColumnPinning={enableColumnPinning}
                    enableColumnResizing={enableColumnResizing}
                    centerHeaders={centerHeaders}
                    effectiveWidths={effectiveWidths}
                    onColumnResize={updateColumnWidth}
                  />
                )
          }
        />
      </div>
    );
  };

  // ─── Footer / Pagination ───
  const footerText =
    totalCountProp !== null && totalCountProp !== undefined
      ? `Showing ${filteredRowCount} of ${totalCountProp}`
      : hasFilters
        ? `${filteredRowCount} of ${totalRowCount} ${plur('item', totalRowCount)}`
        : `${totalRowCount} ${plur('item', totalRowCount)}`;

  // Pagination bar values (TanStack uses 0-based pageIndex, Carbon uses 1-based page)
  const paginationPage = tableState.pagination.pageIndex + 1;
  const paginationPageSize = tableState.pagination.pageSize;
  const paginationTotalItems = isServerPagination
    ? (totalCountProp ?? 0)
    : filteredRowCount;

  // ─── Filter Panel Render Props ───
  const filterPanelRenderProps = useMemo(
    () => ({
      table,
      columnFilters: tableState.columnFilters,
      setColumnFilters: tableState.setColumnFilters,
      globalFilter: tableState.globalFilter,
      setGlobalFilter: tableState.setGlobalFilter,
      resetAllFilters,
      activeFilterCount,
    }),
    [
      table,
      tableState.columnFilters,
      tableState.setColumnFilters,
      tableState.globalFilter,
      tableState.setGlobalFilter,
      resetAllFilters,
      activeFilterCount,
    ]
  );

  return (
    <DataTableProvider
      table={table}
      globalFilter={tableState.globalFilter}
      setGlobalFilter={tableState.setGlobalFilter}
      columnFilters={tableState.columnFilters}
      setColumnFilters={tableState.setColumnFilters}
      sorting={tableState.sorting}
      setSorting={tableState.setSorting}
      columnVisibility={tableState.columnVisibility}
      setColumnVisibility={tableState.setColumnVisibility}
      columnOrder={tableState.columnOrder}
      setColumnOrder={tableState.setColumnOrder}
      columnPinning={tableState.columnPinning}
      setColumnPinning={tableState.setColumnPinning}
      columnSizing={tableState.columnSizing}
      setColumnSizing={tableState.setColumnSizing}
      selectAll={tableState.selectAll}
      toggledNodes={tableState.toggledNodes}
      setSelectAll={tableState.setSelectAll}
      setToggledNodes={tableState.setToggledNodes}
      selectionCount={tableState.selectionCount}
      fireSelectionChange={tableState.fireSelectionChange}
      indeterminateRowIds={indeterminateRowIds}
      isFilterPanelOpen={tableState.isFilterPanelOpen}
      setIsFilterPanelOpen={tableState.setIsFilterPanelOpen}
      isColumnFilterActive={tableState.isColumnFilterActive}
      setIsColumnFilterActive={tableState.setIsColumnFilterActive}
      panelFilterCount={panelFilterCount}
      resetAllFilters={resetAllFilters}
      resetAllCustomizations={handleResetAllCustomizations}
    >
      <div className={styles.dataTableContainer}>
        <div className={styles.dataTableLayout}>
          {/* Filter Panel */}
          {renderFilterPanel && (
            <FilterPanel>
              {renderFilterPanel(filterPanelRenderProps)}
            </FilterPanel>
          )}

          {/* Table Container */}
          <div className={styles.tableContainer}>
            {/* Toolbar */}
            {showToolbar && (
              <TableToolbar
                showGlobalFilter={enableGlobalSearch}
                showFilterToggle={renderFilterPanel !== undefined}
                showColumnFilterToggle={enableColumnFilters}
                showColumnVisibility={enableColumnVisibility}
                showReset={showReset}
                showExport={showExport}
                exportConfig={exportConfig}
                leftToolbarActions={leftToolbarActions}
                fillLeftToolbarActions={fillLeftToolbarActions}
                leadingToolbarActions={leadingToolbarActions}
                rightToolbarActions={rightToolbarActions}
                searchPlaceholder={searchPlaceholder}
                onCollapseAll={
                  hasExpansion && !expandAllInHeader
                    ? () => tableState.handleExpandedChange({})
                    : undefined
                }
                expandAllToggle={expandAllToggle && hasExpansion}
              />
            )}

            {/* Loading progress bar — shows for both the initial load and
                refetches. Sits between toolbar and table content so the user
                sees feedback right where they look. */}
            {showProgress && (
              <ProgressBar
                label="Loading"
                hideLabel
                size="small"
                className={styles.loadingProgress}
              />
            )}

            {/* Optional content between toolbar and table rows (e.g. a map) */}
            {betweenToolbarAndContent}

            {/* Table Content */}
            {renderTableContent()}

            {/* Footer / Pagination */}
            {hasPagination ? (
              <div className={styles.paginationFooter}>
                <Pagination
                  pageSizes={pageSizes}
                  pageSize={paginationPageSize}
                  page={paginationPage}
                  totalItems={paginationTotalItems}
                  onChange={tableState.handlePaginationChange}
                  itemRangeText={itemRangeText}
                  pageRangeText={pageRangeText}
                />
              </div>
            ) : (
              !hideFooter && (
                <div className={styles.tableFooter}>
                  <span>{footerText}</span>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </DataTableProvider>
  );
}
